import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const ShopContext = createContext(null);

const load = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : fallback;
        return Array.isArray(fallback) && !Array.isArray(parsed) ? fallback : parsed;
    } catch {
        return fallback;
    }
};

const save = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full / blocked */ }
};

const BUY_NOW_KEY = "Kanganya_buynow";

const loadBuyNow = () => {
    try {
        const raw = sessionStorage.getItem(BUY_NOW_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

const mergeCarts = (base, extra) => {
    const out = [...base];
    extra.forEach((item) => {
        const idx = out.findIndex((i) => i.product_id === item.product_id && i.size === item.size);
        if (idx >= 0) out[idx] = { ...out[idx], quantity: out[idx].quantity + item.quantity };
        else out.push(item);
    });
    return out;
};

const mergeWishlists = (base, extra) => {
    const out = [...base];
    extra.forEach((p) => { if (!out.some((x) => x.id === p.id)) out.push(p); });
    return out;
};

export const ShopProvider = ({ children }) => {
    const { customer } = useCustomerAuth(); // null = checking, false = guest, object = logged in
    // Har account (aur guest) ki apni alag cart/wishlist hoti hai
    const owner = customer === null ? null : (customer && customer.id ? customer.id : "guest");

    const [cartOwner, setCartOwner] = useState(null); // abhi kiska cart loaded hai
    const [cart, setCart] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [buyNowItem, setBuyNowItem] = useState(loadBuyNow);

    // Purani shared cart/wishlist (jo sab accounts mein dikhti thi) hata do
    useEffect(() => {
        try {
            localStorage.removeItem("Kanganya_cart");
            localStorage.removeItem("Kanganya_wishlist");
        } catch { /* ignore */ }
    }, []);

    // Account badalte hi uska apna cart load karo
    useLayoutEffect(() => {
        if (owner === null || owner === cartOwner) return;
        let nextCart = load(`Kanganya_cart_${owner}`, []);
        let nextWish = load(`Kanganya_wishlist_${owner}`, []);
        if (owner !== "guest") {
            // Login se pehle guest ne jo items daale the wo is account mein aa jaayein
            const guestCart = load("Kanganya_cart_guest", []);
            const guestWish = load("Kanganya_wishlist_guest", []);
            if (guestCart.length) nextCart = mergeCarts(nextCart, guestCart);
            if (guestWish.length) nextWish = mergeWishlists(nextWish, guestWish);
            try {
                localStorage.removeItem("Kanganya_cart_guest");
                localStorage.removeItem("Kanganya_wishlist_guest");
            } catch { /* ignore */ }
        } else if (cartOwner && cartOwner !== "guest") {
            // Logout hua: Buy Now ka item bhi hata do
            try { sessionStorage.removeItem(BUY_NOW_KEY); } catch { /* ignore */ }
            setBuyNowItem(null);
        }
        setCart(nextCart);
        setWishlist(nextWish);
        setCartOwner(owner);
    }, [owner, cartOwner]);

    useEffect(() => { if (cartOwner && cartOwner === owner) save(`Kanganya_cart_${cartOwner}`, cart); }, [cart, cartOwner, owner]);
    useEffect(() => { if (cartOwner && cartOwner === owner) save(`Kanganya_wishlist_${cartOwner}`, wishlist); }, [wishlist, cartOwner, owner]);

    const buildItem = (product, size, quantity) => ({
        product_id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        size,
        quantity,
    });

    const addToCart = (product, size, quantity = 1) => {
        setCart((prev) => {
            const idx = prev.findIndex((i) => i.product_id === product.id && i.size === size);
            if (idx >= 0) {
                const next = [...prev];
                next[idx] = { ...next[idx], quantity: next[idx].quantity + quantity };
                return next;
            }
            return [...prev, buildItem(product, size, quantity)];
        });
    };

    // Buy Now: sirf yahi ek item checkout mein jaata hai, cart ko touch nahi karta
    const startBuyNow = (product, size, quantity = 1) => {
        const item = buildItem(product, size, quantity);
        try { sessionStorage.setItem(BUY_NOW_KEY, JSON.stringify(item)); } catch { /* ignore */ }
        setBuyNowItem(item);
    };

    const clearBuyNow = () => {
        try { sessionStorage.removeItem(BUY_NOW_KEY); } catch { /* ignore */ }
        setBuyNowItem(null);
    };

    const updateQty = (product_id, size, quantity) => {
        setCart((prev) => prev.map((i) => (i.product_id === product_id && i.size === size ? { ...i, quantity } : i)).filter((i) => i.quantity > 0));
    };

    const removeFromCart = (product_id, size) => {
        setCart((prev) => prev.filter((i) => !(i.product_id === product_id && i.size === size)));
    };

    const clearCart = () => setCart([]);

    const toggleWishlist = (product) => {
        setWishlist((prev) => {
            const exists = prev.find((p) => p.id === product.id);
            if (exists) return prev.filter((p) => p.id !== product.id);
            return [...prev, { id: product.id, name: product.name, price: product.price, image: product.image, material: product.material }];
        });
    };

    const isWishlisted = (id) => wishlist.some((p) => p.id === id);

    const cartCount = useMemo(() => cart.reduce((n, i) => n + (i.quantity || 0), 0), [cart]);
    const cartSubtotal = useMemo(() => cart.reduce((n, i) => n + (i.price || 0) * (i.quantity || 0), 0), [cart]);

    const value = {
        cart, wishlist,
        addToCart, updateQty, removeFromCart, clearCart,
        buyNowItem, startBuyNow, clearBuyNow,
        toggleWishlist, isWishlisted,
        cartCount, cartSubtotal,
    };
    return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
};

export const useShop = () => {
    const ctx = useContext(ShopContext);
    if (!ctx) throw new Error("useShop must be used within ShopProvider");
    return ctx;
};
